import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api, ApiError, ArenaDetail, Place } from "../api";
import { ArenaPicker } from "../components/ArenaPicker";
import { Wordmark } from "../components/Wordmark";
import { MapView, Pin } from "../components/MapView";
import { PlaceSearch } from "../components/PlaceSearch";

const NOTE_COSTS = [0, 20, 30];
const NOTE_PLACEHOLDERS = [
  "Narrow the region, not the street.",
  "This one should hurt a little.",
  "The closer. Save it for someone who is stuck.",
];

export function Hide() {
  // Arrives from "Hide from Iqbal →" on the result screen. The revenge loop is
  // the product: the whole reason a round ends is so another one starts.
  const [search] = useSearchParams();
  const revengeOn = search.get("from");

  // The board comes first. A seeker who is told "somewhere on Earth" has no
  // way to use anything they know about the hider, so the clues end up being
  // the entire game. Declaring the board makes "he'd absolutely pick the beach
  // one" a legal move and demotes the clues to tie-breakers.
  const [arena, setArena] = useState<ArenaDetail | null>(null);
  const [picked, setPicked] = useState<Place | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [dropMode, setDropMode] = useState(false);
  const [snapping, setSnapping] = useState(false);
  /** How far the snap moved the pin, when it came from a drop. */
  const [snappedFrom, setSnappedFrom] = useState<number | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notes, setNotes] = useState(["", "", ""]);
  const [reveal, setReveal] = useState("");
  const [name, setName] = useState("");
  const [share, setShare] = useState<{
    url: string;
    token: string;
    pinId: string;
    winRadiusKm: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    if (!picked || !arena) return;
    setBusy(true);
    setError(null);
    try {
      const pin = await api.createPin({
        placeId: picked.id,
        arena: arena.token,
        hiderName: name.trim() || undefined,
        notes: notes.map((n) => n.trim()),
        note: reveal.trim() || undefined,
      });
      setShare({
        url: pin.shareUrl,
        token: pin.hiderToken,
        pinId: pin.pinId,
        winRadiusKm: pin.winRadiusKm,
      });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const pins: Pin[] = picked
    ? [{ lat: picked.lat, lng: picked.lng, label: picked.name, kind: "target" }]
    : [];

  if (share) {
    return (
      <div>
        <Bar />
        <div className="page">
          <div className="label">
            {revengeOn ? `Ready for ${revengeOn}` : "Your spot is live"}
          </div>
          <h1 style={{ fontSize: 34, margin: "10px 0 16px" }}>
            Send this to someone
          </h1>

          <div className="share-card">{share.url}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={() => copy(share.url)}>
              {copied ? "Copied" : "Copy link"}
            </button>
            <Link className="btn" to={`/pin/${share.pinId}/stats?token=${share.token}`}>
              Watch the results
            </Link>
          </div>

          {arena && (
            <p style={{ color: "var(--fog)", marginTop: 22, maxWidth: 520 }}>
              They will be told they are looking in{" "}
              <strong style={{ color: "var(--dusk)" }}>{arena.label}</strong> —{" "}
              {arena.count.toLocaleString()} possible places — and they win by
              getting within {share.winRadiusKm} km.
            </p>
          )}

          <p style={{ color: "var(--fog)", marginTop: 18, maxWidth: 520 }}>
            You score best when they find you, but only after burning most of
            their budget. Somewhere nobody can find scores less than a
            well-judged hide, and the obvious answer scores worst of all.
          </p>
          <p className="clue-sub" style={{ maxWidth: 520 }}>
            Keep the results link private — it is the only way back to this
            spot's stats, and it is not recoverable.
          </p>
        </div>
      </div>
    );
  }

  // Step one: the board. Everything downstream is scoped by it — the map, the
  // search, the snap, the prices and the win radius.
  if (step === 1) {
    return (
      <div>
        <Bar />
        <div className="page">
          <div className="label">
            {revengeOn ? `Hiding from ${revengeOn}` : "Step 1 of 3"}
          </div>
          <h1 style={{ fontSize: 34, margin: "10px 0 6px" }}>
            Where are you hiding?
          </h1>
          <p style={{ color: "var(--fog)", marginTop: 0, maxWidth: 540 }}>
            Not the spot — the board. Tell them the whole world and they can only
            grind through a database. Tell them{" "}
            {revengeOn ? `${revengeOn} ` : "someone "}
            it is your city and the round becomes about how well they know you.
          </p>

          <div style={{ maxWidth: 560, marginTop: 22 }}>
            <ArenaPicker
              value={arena}
              onPick={(next) => {
                setArena(next);
                // A board change invalidates a spot chosen on the old one.
                setPicked(null);
                setSnappedFrom(null);
                setStep(2);
              }}
            />
          </div>

          {arena && (
            <div style={{ marginTop: 20 }}>
              <button className="btn btn-primary" onClick={() => setStep(2)}>
                Keep {arena.label} — pick the spot
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === 3 && picked && arena) {
    return (
      <div>
        <Bar />
        <div className="page">
          <div className="label">Step 3 of 3</div>
          <h1 style={{ fontSize: 34, margin: "10px 0 6px" }}>
            {revengeOn ? `Make ${revengeOn} work for it` : "Write the clues"}
          </h1>
          <p style={{ color: "var(--fog)", marginTop: 0, maxWidth: 540 }}>
            They already know they are looking in{" "}
            <strong style={{ color: "var(--dusk)" }}>{arena.label}</strong>, and
            they can buy facts about{" "}
            <strong style={{ color: "var(--dusk)" }}>{picked.name}</strong> — the
            biome, the nearest World Heritage site. Your clues are the part that
            is about you rather than the pool.
          </p>

          <div className="card" style={{ maxWidth: 560, marginTop: 18 }}>
            <h3 style={{ marginBottom: 4 }}>How you score</h3>
            <p className="clue-sub" style={{ margin: 0 }}>
              Best when they find you having spent almost everything. Clues that
              give it away score less than clues nobody can crack — and both
              score less than cryptic but gettable.
            </p>
          </div>

          {notes.map((value, i) => (
            <div key={i} style={{ maxWidth: 560, marginTop: 20 }}>
              <label className="label" htmlFor={`note-${i}`}>
                Clue {i + 1}
                {i === 0 ? " · free to read" : ` · costs them ${NOTE_COSTS[i]}`}
                {i > 0 && " · optional"}
              </label>
              <input
                id={`note-${i}`}
                value={value}
                maxLength={120}
                autoFocus={i === 0}
                placeholder={NOTE_PLACEHOLDERS[i]}
                onChange={(e) =>
                  setNotes((prev) =>
                    prev.map((n, j) => (j === i ? e.target.value : n)),
                  )
                }
                style={{ marginTop: 6 }}
              />
              <div
                className="clue-sub"
                style={{ textAlign: "right", marginTop: 4 }}
              >
                {value.length}/120
              </div>
            </div>
          ))}

          <div style={{ maxWidth: 560, marginTop: 20 }}>
            <label className="label" htmlFor="reveal-note">
              And a line for after they finish (optional)
            </label>
            <input
              id="reveal-note"
              value={reveal}
              maxLength={140}
              placeholder="This is where I grew up."
              onChange={(e) => setReveal(e.target.value)}
              style={{ marginTop: 6 }}
            />
            <div className="clue-sub" style={{ marginTop: 4 }}>
              Shown only once the round is over. Never a clue.
            </div>
          </div>

          <div style={{ maxWidth: 320, marginTop: 22 }}>
            <label className="label" htmlFor="hider-name">
              Your name (optional)
            </label>
            <input
              id="hider-name"
              value={name}
              maxLength={20}
              placeholder="Shown on the share card"
              onChange={(e) => setName(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>

          {error && (
            <p style={{ color: "var(--flare)", marginTop: 16 }} role="alert">
              {error}
            </p>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 22, flexWrap: "wrap" }}>
            <button className="btn" onClick={() => setStep(2)} disabled={busy}>
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={create}
              disabled={busy || notes[0].trim().length === 0}
            >
              {busy ? "Hiding…" : "Hide here, get a link"}
            </button>
          </div>
          {notes[0].trim().length === 0 && (
            <p className="clue-sub" style={{ marginTop: 10 }}>
              The first clue is required — it is the only one they get for free.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Step two: the same full-bleed map the seeker plays on, framed on the board
  // and refusing to snap outside it. Search or drop.
  return (
    <div className="seek">
      <MapView
        pins={pins}
        fitTo={picked ? pins : null}
        fitBox={arena?.bounds ?? null}
        dropMode={dropMode}
        fullBleed
        layerToggle
        onDrop={async (lat, lng) => {
          if (!dropMode) return;
          setDropMode(false);
          setSnapping(true);
          setError(null);
          try {
            const { place } = await api.nearest(lat, lng, arena?.token);
            setPicked(place);
            setSnappedFrom(place.distanceKm);
          } catch {
            setError("Could not find anywhere hideable on this board near there.");
          } finally {
            setSnapping(false);
          }
        }}
      />

      <div className="hud-top">
        <div className="hud-pill">
          <Wordmark size={17} />
          {arena && <span className="hud-arena">{arena.label}</span>}
        </div>
        <div className="hud-pill">
          <span className="hud-stat">
            <span className="hud-stat-label">
              {revengeOn ? `Hiding from ${revengeOn}` : "Step 2 of 3"}
            </span>
            <span style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: 15 }}>
              Pick your spot
            </span>
          </span>
        </div>
      </div>

      {dropMode && (
        <div className="drop-hint">Tap the map anywhere you like</div>
      )}
      {snapping && <div className="drop-hint">Finding the nearest spot…</div>}

      <div className="hud-side">
        <button
          className={`fab ${dropMode ? "on" : ""}`}
          onClick={() => setDropMode((m) => !m)}
          aria-pressed={dropMode}
        >
          <span className="fab-icon">{dropMode ? "✕" : "📍"}</span>
          <span className="fab-text">{dropMode ? "Cancel" : "Drop"}</span>
        </button>
        <button
          className="fab fab-primary"
          onClick={() => setSearchOpen(true)}
          aria-label="Search for a place"
        >
          <span className="fab-icon">🔎</span>
          <span className="fab-text">Search</span>
        </button>
      </div>

      {/* The chosen spot, and the way forward. */}
      {picked && !dropMode && (
        <div className="hide-sheet">
          <div className="label">Hiding in</div>
          <div className="hide-sheet-name">{picked.name}</div>
          <div className="clue-sub">
            {[picked.admin1, picked.countryName].filter(Boolean).join(" · ")}
          </div>
          {snappedFrom !== null && snappedFrom > 0 && (
            <div className="clue-sub" style={{ marginTop: 6 }}>
              Snapped {snappedFrom.toLocaleString()} km from where you tapped —
              only places on this board can be hidden in.
            </div>
          )}
          {error && (
            <p style={{ color: "var(--flare)", marginTop: 8 }} role="alert">
              {error}
            </p>
          )}
          <button
            className="btn btn-primary"
            style={{ width: "100%", marginTop: 14 }}
            onClick={() => setStep(3)}
          >
            Next — write the clues
          </button>
        </div>
      )}

      {!picked && !dropMode && !snapping && (
        <div className="hide-sheet">
          <div className="hide-sheet-name" style={{ fontSize: 19 }}>
            {revengeOn
              ? `Somewhere ${revengeOn} will never find`
              : `Somewhere in ${arena?.label ?? "the world"}`}
          </div>
          <div className="clue-sub" style={{ marginTop: 4 }}>
            {arena
              ? `${arena.count.toLocaleString()} places qualify. `
              : ""}
            Drop a pin or search. Somewhere too obvious is easy to find and
            scores badly; somewhere nobody could reach scores badly too.
          </div>
          <button
            className="btn btn-ghost"
            style={{ width: "100%", marginTop: 10 }}
            onClick={() => setStep(1)}
          >
            Change the board
          </button>
        </div>
      )}

      {/* Search drawer, same pattern as the seeker's clue drawer. */}
      <div
        className={`drawer-scrim ${searchOpen ? "on" : ""}`}
        onClick={() => setSearchOpen(false)}
      />
      <aside className={`drawer ${searchOpen ? "on" : ""}`} aria-label="Search">
        <div className="drawer-head">
          <div className="label">Search the pool</div>
          <button
            className="drawer-close"
            onClick={() => setSearchOpen(false)}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="drawer-scroll">
          <PlaceSearch
            placeholder="Town or city…"
            arena={arena?.token}
            onPick={(place) => {
              setPicked(place);
              setSnappedFrom(null);
              setSearchOpen(false);
            }}
          />
        </div>
      </aside>
    </div>
  );
}

function messageFor(err: unknown): string {
  const code = err instanceof ApiError ? err.code : "";
  switch (code) {
    case "arena_too_small":
      return "Too few places on that board to make a round of it.";
    case "place_outside_arena":
      return "That spot is not on the board you picked.";
    case "bad_arena":
      return "That board is not one we know.";
    case "note_required":
      return "The first clue is required.";
    case "note_too_long":
      return "Keep each clue under 120 characters.";
    case "note_no_links":
      return "Clues cannot contain links, handles or email addresses.";
    case "note_rejected":
      return "That wording will not do — other people read these.";
    default:
      return "That pin did not drop. Check your connection and try again.";
  }
}

function Bar() {
  return (
    <div className="topbar">
      <Wordmark size={20} />
      <Link to="/how-to-play" className="btn btn-ghost" >
        How to play
      </Link>
    </div>
  );
}
